import React, { useCallback, useEffect, useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { Layers as LayersIcon } from "@mui/icons-material";
import { Box, Button, MenuItem, Select, Skeleton, TextField, Typography } from "@mui/material";

import { Table, TableBody, TableHead } from "ui";

import analytics from "fond/analytics";
import { BodyCell, CompactFormControl, HeaderCell } from "fond/export/ExportTable/ExportTable.styles";
import { ResourceAutocomplete } from "fond/import/modal/ProjectVersionSelection/ProjectVersionSelection";
import { InferredIcon } from "fond/map/Legend";
import { Configuration } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { CoreConfigurationLayerMapping } from "fond/types/version";
import { selectLayersFromConfig } from "fond/utils/configurations";
import { fuzzyMatchLayers } from "fond/utils/layers";

import {
  applyDefaultIdAttributes,
  convertLayerMappings,
  deriveLayerMappingAttributes,
  ignoreLayerOption,
  resolveLayerConfigurationInputLayers,
  resolveLayerConfigurationNameFromFilename,
} from "./helper";
import { FormData } from "./SetupForm";

import { IconBox, LayerHeaderBox, LayerTableContainer, LayerTableRow } from "./LayerSelectionStep.styles";

interface LayerSelectionStepProps {
  config: Configuration;
  isReadOnly?: boolean;
  versionId: string;
}

const LayerSelectionStep: React.FC<LayerSelectionStepProps> = ({ config, isReadOnly, versionId }) => {
  const { change } = useForm();
  const {
    input: { value: layersConfiguration },
  } = useField<FormData["CoreConfigInput"]["LayersConfiguration"]["Data"]>("CoreConfigInput.LayersConfiguration.Data");

  const {
    meta: { error: stepError, touched, dirty },
    input: { value: layerMappings },
  } = useField<FormData["CoreConfigInput"]["LayerMappings"]>("CoreConfigInput.LayerMappings", {
    validate: (value) => {
      if (!value || Object.keys(value).length === 0) {
        return "At least one layer must be mapped.";
      }
      return undefined;
    },
  });

  const layerConfigs = useMemo(() => [ignoreLayerOption, ...selectLayersFromConfig(config, ["LAYER"])] as LayerConfig[], [config]);

  // Get the C2S layers from the configuration
  const c2sLayers = useMemo(() => {
    const layerNames = layersConfiguration ? Array.from(resolveLayerConfigurationInputLayers(layersConfiguration)) : [];
    return layerNames.map((name) => ({ name, displayName: resolveLayerConfigurationNameFromFilename(layersConfiguration!, name) }));
  }, [layersConfiguration]);

  useEffect(() => {
    // Auto-map layers using fuzzy matching only if there are no existing mappings
    if (dirty || (layerMappings && layerMappings.length > 0)) return;

    const suggestions = fuzzyMatchLayers(
      c2sLayers.map((layer) => layer.name),
      layerConfigs
    );

    // Count auto-mapped layers
    const autoMappedCount = Object.values(suggestions || {}).filter((v) => v !== null).length;

    // Track auto-mapping (only if not in readonly mode)
    if (!isReadOnly) {
      analytics.track("collaboration.cts.layers.auto_mapped", {
        versionId,
        totalLayers: c2sLayers.length,
        autoMappedCount,
      });
    }

    // Initialize mappings for all C2S layers
    change(
      "CoreConfigInput.LayerMappings",
      convertLayerMappings(
        Object.fromEntries(c2sLayers.map(({ name: layerName }) => [layerName, suggestions?.[layerName] ?? null])),
        layersConfiguration ?? undefined
      )
    );
  }, [c2sLayers, change, dirty, layerConfigs, layerMappings, layersConfiguration, isReadOnly, versionId]);

  const handleLayerMappingChange = useCallback(
    (c2sLayerName: string, selectedLayer: LayerConfig | null) => {
      const newValue: FormData["CoreConfigInput"]["LayerMappings"] = layerMappings?.filter((m) => m.Name !== c2sLayerName);

      if (selectedLayer?.Key) {
        newValue.push({
          Name: c2sLayerName,
          Layer: {
            LayerKey: selectedLayer.Key,
          },
          ...applyDefaultIdAttributes(
            deriveLayerMappingAttributes(layersConfiguration!, c2sLayerName),
            selectedLayer.Attributes?.map((attribute) => attribute.Name) ?? []
          ),
        });
      }

      // Track layer mapping change
      analytics.track("collaboration.cts.layers.mapping_changed", {
        versionId,
        c2sLayerName,
        selectedLayerKey: selectedLayer?.Key,
        wasIgnored: selectedLayer === ignoreLayerOption,
      });

      change("CoreConfigInput.LayerMappings", newValue);
    },
    [change, layerMappings, layersConfiguration, versionId]
  );

  const handleAttributeChange = useCallback(
    (c2sLayerName: string, patch: Partial<CoreConfigurationLayerMapping>) => {
      change(
        "CoreConfigInput.LayerMappings",
        layerMappings.map((mapping) => (mapping.Name === c2sLayerName ? { ...mapping, ...patch } : mapping))
      );
    },
    [change, layerMappings]
  );

  // Reset the layer mappings back to the auto-suggested mapping derived from the layers configuration, discarding
  // any manual changes
  const handleResetFromLayersConfiguration = useCallback(() => {
    const suggestions = fuzzyMatchLayers(
      c2sLayers.map((layer) => layer.name),
      layerConfigs
    );

    analytics.track("collaboration.cts.layers.reset_from_config", {
      versionId,
      mappedLayerCount: layerMappings.length,
    });

    change(
      "CoreConfigInput.LayerMappings",
      convertLayerMappings(
        Object.fromEntries(c2sLayers.map(({ name: layerName }) => [layerName, suggestions?.[layerName] ?? null])),
        layersConfiguration ?? undefined
      )
    );
  }, [c2sLayers, change, layerConfigs, layerMappings.length, layersConfiguration, versionId]);

  if (!layersConfiguration) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Layer Selection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please upload a layers configuration first.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Layer Association
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Associate your layers configuration with layers in your project.
      </Typography>

      <Box>
        <LayerHeaderBox>
          <Box display="flex" margin="auto 0">
            <LayersIcon
              style={{
                marginLeft: 4,
                marginRight: 8,
              }}
            />
            <Typography variant="h6" component="p">
              {c2sLayers.length} Layers
            </Typography>
          </Box>
          {!isReadOnly && (
            <Button
              size="small"
              onClick={handleResetFromLayersConfiguration}
              disabled={layerMappings.length === 0}
              data-testid="reset-from-layers-configuration-button"
            >
              Reset from layers configuration
            </Button>
          )}
        </LayerHeaderBox>

        <LayerTableContainer>
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <LayerTableRow>
                <HeaderCell width={194}>
                  Configuration Item{" "}
                  {stepError && touched && (
                    <Typography color="error" component="span">
                      * {stepError}
                    </Typography>
                  )}
                </HeaderCell>
                <HeaderCell width={194}>Select layer</HeaderCell>
                <HeaderCell width={140}>Element type</HeaderCell>
                <HeaderCell width={130}>Graph type</HeaderCell>
                <HeaderCell width={130}>FOND ID</HeaderCell>
                <HeaderCell width={130}>Start ID</HeaderCell>
                <HeaderCell width={130}>End ID</HeaderCell>
              </LayerTableRow>
            </TableHead>
            <TableBody>
              {c2sLayers.map(({ name: c2sLayerName, displayName }) => {
                const mapping = layerMappings.find(({ Name }) => Name === c2sLayerName);
                const value = layerConfigs.find((lc) => lc.Key === mapping?.Layer.LayerKey) || ignoreLayerOption;
                // The other per-layer attributes only make sense once a real layer has been mapped.
                const isMapped = !!mapping;
                // Physical/Network and Node/Edge are mutually exclusive per layer.
                let elementType = "";
                if (mapping?.PhysicalElement) elementType = "physical";
                else if (mapping?.NetworkElement) elementType = "network";

                let graphType = "";
                if (mapping?.NodeElement) graphType = "node";
                else if (mapping?.EdgeElement) graphType = "edge";

                // Get the list of attribute names available on the mapped layer, used for ID field options.
                const attributeNames = value.Attributes?.map((attribute) => attribute.Name) ?? [];
                const idFieldOptions = (current: string | null) =>
                  current && !attributeNames.includes(current) ? [...attributeNames, current] : attributeNames;

                return (
                  <LayerTableRow key={c2sLayerName} sx={{ height: "42px" }}>
                    <BodyCell width={194}>
                      <Typography variant="body2">{displayName}</Typography>
                    </BodyCell>
                    <BodyCell>
                      <CompactFormControl width={194} size="small">
                        {!config ? (
                          <Skeleton height={42} />
                        ) : (
                          <ResourceAutocomplete
                            sx={{ backgroundColor: "white" }}
                            value={value}
                            onChange={(_, newValue) => handleLayerMappingChange(c2sLayerName, newValue)}
                            options={layerConfigs}
                            getOptionLabel={(option) => option?.Label || ""}
                            disabled={isReadOnly}
                            renderOption={(option: LayerConfig) => {
                              return (
                                <Box data-testid="option-configuration" display="flex">
                                  {option.ID !== ignoreLayerOption.ID ? (
                                    <IconBox>
                                      <InferredIcon entity={option} config={config} />
                                    </IconBox>
                                  ) : (
                                    <IconBox>
                                      <LayersIcon />
                                    </IconBox>
                                  )}
                                  <Typography data-testid="option-label">{option.Label}</Typography>
                                </Box>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment:
                                    value.ID && config ? (
                                      <IconBox>
                                        <InferredIcon entity={value} config={config} />
                                      </IconBox>
                                    ) : (
                                      <IconBox>
                                        <LayersIcon />
                                      </IconBox>
                                    ),
                                }}
                                placeholder="Select a layer..."
                                variant="outlined"
                              />
                            )}
                          />
                        )}
                      </CompactFormControl>
                    </BodyCell>
                    <BodyCell width={140}>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={elementType}
                        disabled={isReadOnly || !isMapped}
                        onChange={(e) => {
                          const newElementType = e.target.value;
                          handleAttributeChange(c2sLayerName, {
                            PhysicalElement: newElementType === "physical",
                            NetworkElement: newElementType === "network",
                          });
                        }}
                        data-testid="element-type-select"
                      >
                        <MenuItem value="">--</MenuItem>
                        <MenuItem value="physical">Physical</MenuItem>
                        <MenuItem value="network">Network</MenuItem>
                      </Select>
                    </BodyCell>
                    <BodyCell width={130}>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={graphType}
                        disabled={isReadOnly || !isMapped}
                        onChange={(e) => {
                          const newGraphType = e.target.value;
                          if (newGraphType === "node") {
                            // Clearing Start/End IDs for nodes since they are only relevant for edges.
                            handleAttributeChange(c2sLayerName, {
                              NodeElement: true,
                              EdgeElement: false,
                              StartIDAttribute: null,
                              EndIDAttribute: null,
                            });
                          } else if (newGraphType === "edge") {
                            // Try to apply default ID attributes for the edge.
                            const { StartIDAttribute, EndIDAttribute } = applyDefaultIdAttributes(
                              { FondIDAttribute: null, StartIDAttribute: null, EndIDAttribute: null, EdgeElement: true },
                              attributeNames
                            );
                            handleAttributeChange(c2sLayerName, {
                              NodeElement: false,
                              EdgeElement: true,
                              StartIDAttribute,
                              EndIDAttribute,
                            });
                          } else {
                            handleAttributeChange(c2sLayerName, { NodeElement: false, EdgeElement: false });
                          }
                        }}
                        data-testid="graph-type-select"
                      >
                        <MenuItem value="">--</MenuItem>
                        <MenuItem value="node">Node</MenuItem>
                        <MenuItem value="edge">Edge</MenuItem>
                      </Select>
                    </BodyCell>
                    <BodyCell width={130}>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={mapping?.FondIDAttribute ?? ""}
                        disabled={isReadOnly || !isMapped}
                        onChange={(e) => handleAttributeChange(c2sLayerName, { FondIDAttribute: e.target.value || null })}
                        data-testid="fond-id-select"
                      >
                        <MenuItem value="">--</MenuItem>
                        {idFieldOptions(mapping?.FondIDAttribute ?? null).map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </BodyCell>
                    <BodyCell width={130}>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={mapping?.StartIDAttribute ?? ""}
                        disabled={isReadOnly || !isMapped || graphType === "node"}
                        onChange={(e) => handleAttributeChange(c2sLayerName, { StartIDAttribute: e.target.value || null })}
                        data-testid="start-id-select"
                      >
                        <MenuItem value="">--</MenuItem>
                        {idFieldOptions(mapping?.StartIDAttribute ?? null).map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </BodyCell>
                    <BodyCell width={130}>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={mapping?.EndIDAttribute ?? ""}
                        disabled={isReadOnly || !isMapped || graphType === "node"}
                        onChange={(e) => handleAttributeChange(c2sLayerName, { EndIDAttribute: e.target.value || null })}
                        data-testid="end-id-select"
                      >
                        <MenuItem value="">--</MenuItem>
                        {idFieldOptions(mapping?.EndIDAttribute ?? null).map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </BodyCell>
                  </LayerTableRow>
                );
              })}
            </TableBody>
          </Table>
        </LayerTableContainer>
      </Box>
    </Box>
  );
};

export default LayerSelectionStep;
